import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Shield, TrendingUp, Video, ArrowRight, Play, Globe } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Handler for the Demo Button
  const handleDemoClick = () => {
    alert("REVIEWER CREDENTIALS:\n\nEmail: demo@millionorbustapp.com\nPassword: Demo2026$");
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      
      {/* NAV BAR (Simple Top Nav for About/Wall) */}
      <div style={styles.topNav}>
        <div style={styles.logo}>M.O.B.</div>
        <div style={styles.navLinks}>
            {!user ? (
               <button onClick={() => navigate('/login')} style={styles.loginBtnNav}>LOG IN</button>
            ) : (
               <span style={styles.userEmail}>{user.email}</span>
            )}
            <button onClick={() => navigate('/about')} style={styles.navLink}>ABOUT</button>
            <button onClick={() => navigate('/million-wall')} style={styles.navLink}>MILLION WALL</button>
            <button onClick={() => navigate('/profile-view/demo')} style={styles.navLink}>COMMUNITY</button>
        </div>
      </div>

      {/* HERO SECTION */}
      <div style={styles.hero}>
        <div style={styles.heroContent}>
            <h1 style={styles.title}>MILLION OR BUST <span style={styles.tag}>APP</span></h1>
            <h2 style={styles.subtitle}>FROM $0 TO EXIT STRATEGY // POWERED BY GEMINI</h2>
            
            <div style={styles.btnGroup}>
              <button onClick={() => navigate('/login')} style={styles.primaryBtn}>
                  ENTER THE SYSTEM
                  <ArrowRight size={20} />
              </button>

              {/* --- NEW: DEMO SHORTCUT FOR REVIEWERS --- */}
              <div style={styles.demoContainer}>
                <p style={styles.demoLabel}>Reviewing the App?</p>
                <button 
                  onClick={handleDemoClick}
                  style={styles.demoLink}
                >
                  View Demo Credentials
                </button>
              </div>
              {/* --------------------------------------- */}

              <button onClick={() => navigate('/about')} style={styles.secondaryBtn}>
                  READ THE MISSION
              </button>
            </div>
        </div>
      </div>

      {/* CORE TECH STACK (The Big 3) */}
      <div style={styles.featureSection}>
        <div style={styles.featureCard}>
          <div style={styles.iconBox}><TrendingUp size={30} color="#D4AF37"/></div>
          <h3 style={styles.featureTitle}>GEMINI MARKET ANALYST</h3>
          <p style={styles.featureText}>Type a vague idea, get a psychological profile of your exact customer. Know who you are selling to before you build.</p>
        </div>
        <div style={styles.featureCard}>
          <div style={styles.iconBox}><Shield size={30} color="#D4AF37"/></div>
          <h3 style={styles.featureTitle}>SALES LAB SANDBOX</h3>
          <p style={styles.featureText}>Roleplay against a skeptical AI buyer. Get rejected 100 times in the sim so you win in real life.</p>
        </div>
        <div style={styles.featureCard}>
          <div style={styles.iconBox}><Video size={30} color="#D4AF37"/></div>
          <h3 style={styles.featureTitle}>CONTENT INTELLIGENCE</h3>
          <p style={styles.featureText}>AI Video Grader & Script Architect. Stop guessing. Turn boring scripts into viral hits mathematically.</p>
        </div>
      </div>

      {/* THE 10-STEP OPERATIONAL ROADMAP */}
      <div style={styles.roadmapSection}>
        <h3 style={styles.sectionTitle}>THE EXECUTION PROTOCOL</h3>
        
        <div style={styles.stepContainer}>
          {/* STEP 0 */}
          <div style={{...styles.step, borderLeft: '3px solid #ff3333'}}>
            <div style={styles.stepHeader}>
              <span style={{...styles.stepNum, color: '#ff3333'}}>STEP 0:</span> 
              <span style={styles.stepTitle}>ALL IN (THE COMMITMENT)</span>
            </div>
            <p style={styles.stepText}>This step requires you to burn the boats—setting a budget, defining your "Why," and signing a literal contract with yourself to never quit. It locks in your "Seed Budget" so every expense is tracked against your runway.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: DIGITAL FOUNDER'S CONTRACT</div>
          </div>

          {/* STEP 1 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 1:</span> <span style={styles.stepTitle}>WHO ARE YOU SELLING TO?</span></div>
            <p style={styles.stepText}>Identify your exact target demographic. Type a vague idea (e.g., "gloves for mechanics"), and the AI generates a detailed "Customer Persona."</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: GEMINI MARKET ANALYST</div>
          </div>

          {/* STEP 2 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 2:</span> <span style={styles.stepTitle}>THE UGLY PROTOTYPE</span></div>
            <p style={styles.stepText}>Create a functional, non-pretty version of your product. The AI scans your product description and flags "feature creep" to keep you focused on speed.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: MVP BLUEPRINT TOOL</div>
          </div>

          {/* STEP 3 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 3:</span> <span style={styles.stepTitle}>THE PITCH (SALES LAB)</span></div>
            <p style={styles.stepText}>Refine your elevator pitch until it is undeniable. Gemini acts as the skeptic customer, listening to your verbal pitch and giving a real-time score.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: SALES LAB SANDBOX</div>
          </div>

          {/* STEP 4 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 4:</span> <span style={styles.stepTitle}>REFINEMENT</span></div>
            <p style={styles.stepText}>Fix mechanical failures in your offer. Input objections you heard in real life, and Gemini analyzes patterns to suggest specific pivots.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: FEEDBACK DIAGNOSTIC</div>
          </div>

          {/* STEP 5 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 5:</span> <span style={styles.stepTitle}>THE CONTENT ENGINE</span></div>
            <p style={styles.stepText}>Generate organic traffic daily. The AI Video Grader gives you an A-F score on your hook and retention.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: CONTENT INTELLIGENCE LAB</div>
          </div>

          {/* STEP 6 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 6:</span> <span style={styles.stepTitle}>SCALING THE REACH</span></div>
            <p style={styles.stepText}>Mass-produce content without burnout. One-click editing adds B-roll and captions instantly.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: STUDIO LAB (AUTO-EDIT)</div>
          </div>

          {/* STEP 7 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 7:</span> <span style={styles.stepTitle}>SYSTEMS (REPLACING YOURSELF)</span></div>
            <p style={styles.stepText}>Move from "doing" to "managing." Gemini automatically writes SOPs for your future employees.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: SOP GENERATOR</div>
          </div>

          {/* STEP 8 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 8:</span> <span style={styles.stepTitle}>THE $10K MILESTONE</span></div>
            <p style={styles.stepText}>The "Proof of Life" stage. Dashboard shifts focus to Profit Margins and LTV.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: REVENUE TRACKER</div>
          </div>

          {/* STEP 9 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 9:</span> <span style={styles.stepTitle}>$100K/MO (THE MACHINE)</span></div>
            <p style={styles.stepText}>Run a massive operation. Sales Lab updates to "Investor Mode" to practice pitching banks.</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: EXECUTIVE ANALYTICS</div>
          </div>

          {/* STEP 10 */}
          <div style={styles.step}>
            <div style={styles.stepHeader}><span style={styles.stepNum}>STEP 10:</span> <span style={styles.stepTitle}>THE EXIT STRATEGY</span></div>
            <p style={styles.stepText}>Prepare the business to be sold. The calculator estimates valuation and generates a "Deal Packet."</p>
            <div style={styles.toolBadge}>SYSTEM UNLOCK: EXIT VALUATION CALCULATOR</div>
          </div>

        </div>
      </div>
      
      {/* FOOTER (Updated with Trust Signals) */}
      <div style={styles.footer}>
         <div>MILLION OR BUST © 2026</div>
         <div style={{fontSize:'0.8rem', color:'#666', marginTop:'5px'}}>BUILT ON GOOGLE CLOUD + FIREBASE</div>
         
         <div style={styles.legalLinks}>
             <span style={styles.legalItem}>PRIVACY POLICY</span>
             <span style={styles.legalItem}>TERMS OF SERVICE</span>
         </div>
      </div>
    </div>
  );
};

const styles = {
  container: { background: '#000', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff', fontFamily: 'Inter, sans-serif' },
  
  topNav: { width: '100%', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #222', background: '#0a0a0a' },
  logo: { fontFamily: 'Orbitron', fontWeight: 'bold', color: '#D4AF37', fontSize: '1.2rem' },
  navLinks: { display: 'flex', gap: '20px' },
  navLink: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontFamily: 'Orbitron', fontSize: '0.8rem', ':hover': { color: '#fff' } },

  hero: { textAlign: 'center', padding: '100px 20px', width: '100%', maxWidth: '800px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  heroContent: { animation: 'fadeIn 1s ease-in' },
  title: { fontFamily: 'Orbitron', fontSize: '3rem', color: '#fff', letterSpacing: '5px', margin: 0 },
  tag: { color: '#D4AF37', fontSize: '1.5rem' },
  subtitle: { color: '#666', letterSpacing: '4px', fontSize: '1rem', marginTop: '20px', marginBottom: '40px', fontFamily: 'Orbitron' },
  
  btnGroup: { display: 'flex', gap: '20px', flexDirection: 'column', alignItems: 'center' },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', padding: '20px 40px', background: '#D4AF37', color: '#000', border: 'none', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Orbitron', minWidth: '300px' },
  secondaryBtn: { padding: '20px 40px', background: 'transparent', color: '#D4AF37', border: '1px solid #D4AF37', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Orbitron', minWidth: '300px' },
  
  // DEMO BUTTON STYLES
  demoContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' },
  demoLabel: { color: '#666', fontSize: '0.8rem', margin: 0, letterSpacing: '1px' },
  demoLink: { background: 'transparent', border: '1px solid #333', color: '#888', padding: '8px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Orbitron', transition: 'all 0.2s', ':hover': { borderColor: '#D4AF37', color: '#D4AF37' } },

  // FEATURES STYLES
  featureSection: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px', padding: '60px 20px', background: '#0a0a0a', width: '100%', borderTop: '1px solid #222', borderBottom: '1px solid #222' },
  featureCard: { background: '#000', border: '1px solid #333', padding: '30px', width: '300px', textAlign: 'center', borderRadius: '8px' },
  iconBox: { marginBottom: '20px', display: 'flex', justifyContent: 'center' },
  featureTitle: { color: '#D4AF37', fontFamily: 'Orbitron', marginBottom: '10px', fontSize: '1.1rem' },
  featureText: { color: '#888', fontSize: '0.9rem', lineHeight: '1.5' },

  roadmapSection: { width: '100%', maxWidth: '800px', padding: '80px 20px' },
  sectionTitle: { textAlign: 'center', fontFamily: 'Orbitron', color: '#D4AF37', fontSize: '2rem', marginBottom: '60px', borderBottom: '2px solid #D4AF37', paddingBottom: '10px', display: 'inline-block' },
  stepContainer: { display: 'flex', flexDirection: 'column', gap: '30px' },
  step: { background: '#111', padding: '30px', borderLeft: '3px solid #D4AF37', position: 'relative', borderRadius: '0 8px 8px 0' },
  stepHeader: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' },
  stepNum: { color: '#D4AF37', fontWeight: 'bold', fontFamily: 'Orbitron', fontSize: '1.2rem', whiteSpace: 'nowrap' },
  stepTitle: { color: '#fff', fontWeight: 'bold', fontFamily: 'Orbitron', fontSize: '1.2rem' },
  stepText: { color: '#ccc', lineHeight: '1.6', fontSize: '0.95rem' },
  toolBadge: { marginTop: '20px', display: 'inline-block', background: '#1a1a1a', color: '#D4AF37', padding: '8px 15px', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'Courier New', border: '1px solid #333', fontWeight: 'bold' },

  footer: { padding: '40px', borderTop: '1px solid #222', width: '100%', textAlign: 'center', color: '#444', fontFamily: 'Orbitron', fontSize: '0.9rem' },
  legalLinks: { marginTop: '15px', display: 'flex', gap: '20px', justifyContent: 'center' },
  legalItem: { fontSize: '0.7rem', color: '#333', cursor: 'pointer' },

  // NEW STYLES
  loginBtnNav: { background: '#D4AF37', color: '#000', border: 'none', padding: '10px 20px', fontSize: '0.9rem', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Orbitron', marginRight: '20px', borderRadius: '4px' },
  userEmail: { color: '#D4AF37', fontFamily: 'Orbitron', fontSize: '0.8rem', marginRight: '20px', border: '1px solid #333', padding: '5px 10px', borderRadius: '4px' }
};

export default Landing;